import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import {
  getSessionStorage,
  loadFromStorageKeyPrefix,
  persistEntryPrefixed,
} from "./reduxStorageCommon"

interface ExperienceState {
  selectorId?: string;
  resLinkParamsId?: string;
  organizationId?: string;
  // Room for future experience-related configuration
}

const ExperiencePrefix = "experience_";

const getStorage = () => {
  return getSessionStorage();
};

const loadFromStorage = (key: string) => {
  return loadFromStorageKeyPrefix(key, ExperiencePrefix, getStorage());
};

const initialState: ExperienceState = {
  selectorId: loadFromStorage('selectorId'),
  resLinkParamsId: loadFromStorage('resLinkParamsId'),
  organizationId: loadFromStorage('organizationId'),
};

export const experienceSlice = createSlice({
  name: 'experience',
  initialState,
  reducers: {
    setSelectorId: (state, action: PayloadAction<string | undefined>) => {
      state.selectorId = action.payload;
    },
    setResLinkParamsId: (state, action: PayloadAction<string | undefined>) => {
      state.resLinkParamsId = action.payload;
    },
    setOrganizationId: (state, action: PayloadAction<string | undefined>) => {
      state.organizationId = action.payload;
    },
    setExperienceData: (state, action: PayloadAction<Partial<ExperienceState>>) => {
      Object.assign(state, action.payload);
    },
    clearExperienceData: (state) => {
      state.selectorId = undefined;
      state.resLinkParamsId = undefined;
      state.organizationId = undefined;
      // Clear any future experience fields here
    },
  },
});

export const {
  setSelectorId,
  setResLinkParamsId,
  setOrganizationId,
  setExperienceData,
  clearExperienceData,
} = experienceSlice.actions;

// Selectors
export const selectSelectorId = (state: RootState) => state.experience.selectorId;
export const selectHasSelectorId = (state: RootState) => !!state.experience.selectorId;
export const selectResLinkParamsId = (state: RootState) => state.experience.resLinkParamsId;
export const selectHasResLinkParamsId = (state: RootState) => !!state.experience.resLinkParamsId;
export const selectOrganizationId = (state: RootState) => state.experience.organizationId;
export const selectHasOrganizationId = (state: RootState) => !!state.experience.organizationId;
export const selectExperienceData = (state: RootState) => state.experience;

// Persistence setup
const persistEntry = (key: string, value: any) => {
  persistEntryPrefixed(ExperiencePrefix, key, value, getSessionStorage());
};

export const setupExperiencePersistence = (store: any) => {
  store.subscribe(() => {
    const experienceState = store.getState().experience;

    persistEntry('selectorId', experienceState.selectorId);
    persistEntry('resLinkParamsId', experienceState.resLinkParamsId);
    persistEntry('organizationId', experienceState.organizationId);
    // Add persistence for future fields here
  });
};

export default experienceSlice.reducer;
